import { getReportMetricKind } from './report-table-styles';
import { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { OpenInNewRounded } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { AppVarsContext } from '../../context/AppVarsContext';
import { getPlainSurfaceCardSx } from './reportSurfaceStyles';

const TONE_MAP = {
  success: { accent: '#1e7b4d', background: '#eaf7f0' },
  warning: { accent: '#b26a00', background: '#fff5e2' },
  error: { accent: '#c13f3f', background: '#fdecec' },
  default: { accent: '#3f4b66', background: '#edf1f8' },
};

const formatMetricValue = (value) => {
  if (value === null || typeof value === 'undefined' || value === '') return '—';
  return value;
};

const EmailPreviewThumb = ({
  alt,
  captureUrl,
  directImageUrl,
  placeholder,
  providerLabel,
  thumbRequest,
}) => {
  const theme = useTheme();
  const { appVars } = useContext(AppVarsContext);
  const [imageSrc, setImageSrc] = useState(directImageUrl || null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl = '';
    const screenshotUrl = captureUrl && appVars?.api_url
      ? `${appVars.api_url}/screenshot?d=${encodeURIComponent(captureUrl)}&id=${encodeURIComponent(placeholder || alt || 'email-preview')}&type=jpeg`
      : '';

    setFailed(false);

    if (directImageUrl) {
      setImageSrc(directImageUrl);
      return undefined;
    }

    if (!thumbRequest?.endpoint || !appVars?.api_url) {
      setImageSrc(screenshotUrl || null);
      return undefined;
    }

    const fetchPreview = async () => {
      try {
        const response = await fetch(`${appVars.api_url}${thumbRequest.endpoint}`, {
          method: thumbRequest.method || 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(thumbRequest.headers || {}),
          },
          body: JSON.stringify(thumbRequest.body || {}),
        });

        if (!response.ok) throw new Error(`Preview request failed (${response.status})`);

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        if (active) {
          setImageSrc(objectUrl);
        }
      } catch (error) {
        if (active) {
          setFailed(true);
          setImageSrc(screenshotUrl || null);
        }
      }
    };

    fetchPreview();

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [alt, appVars?.api_url, captureUrl, directImageUrl, placeholder, thumbRequest, providerLabel]);

  if (imageSrc && !failed) {
    return (
      <Box
        component="img"
        src={imageSrc}
        alt={alt || providerLabel}
        onError={() => setFailed(true)}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          backgroundColor: '#ffffff',
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.18)} 0%, ${alpha(theme.palette.secondary.light, 0.14)} 100%)`,
        display: 'grid',
        placeItems: 'center',
        textAlign: 'center',
        p: 2,
      }}
    >
      <Box>
        <Typography variant="h4" color="primary" sx={{ fontWeight: 800, letterSpacing: 1 }}>
          {placeholder || providerLabel?.slice(0, 2)?.toUpperCase() || 'EM'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Preview unavailable
        </Typography>
      </Box>
    </Box>
  );
};

export const formatEmailDate = (value) => {
  if (!value) return 'No send date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(date);
};

export const EmailMetricGrid = ({ items = [] }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          lg: 'repeat(4, minmax(0, 1fr))',
        },
      }}
    >
      {items.map((item) => (
        <Card key={item.label} sx={getPlainSurfaceCardSx(theme)}>
          <CardContent sx={{ display: 'grid', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
              {item.label}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              {formatMetricValue(item.value)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.helper || ' '}
            </Typography>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export const EmailInsightsCard = ({ title, subtitle, items = [] }) => {
  const theme = useTheme();

  return (
    <Card sx={getPlainSurfaceCardSx(theme)}>
      <CardContent sx={{ display: 'grid', gap: 1.25 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ display: 'grid', gap: 1 }}>
          {items.map((item, index) => {
            const tone = TONE_MAP[item?.tone] || TONE_MAP.default;
            return (
              <Box
                key={`${item?.label || 'insight'}-${index}`}
                sx={{
                  borderRadius: 2,
                  px: 1.5,
                  py: 1.25,
                  borderLeft: `4px solid ${tone.accent}`,
                  backgroundColor: tone.background,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {item?.label || 'Insight'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {item?.text || ''}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

export const EmailGallerySection = ({
  providerLabel,
  title,
  subtitle,
  rows = [],
}) => {
  const theme = useTheme();

  return (
    <Card sx={getPlainSurfaceCardSx(theme)}>
      <CardContent>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>

          {rows.length ? (
            <Box
              sx={{
                display: 'grid',
                gap: 1.25,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(auto-fill, minmax(240px, 290px))',
                },
                justifyContent: 'start',
              }}
            >
              {rows.map((row) => (
                <Card
                  key={row.id}
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 4,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                    background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha('#ffffff', 0.88)} 30%, ${alpha('#ffffff', 0.96)} 100%)`,
                    boxShadow: `0 24px 60px ${alpha(theme.palette.primary.main, 0.1)}`,
                    height: '100%',
                    alignContent: 'start',
                  }}
                >
                  <Box sx={{ p: 1.1, pb: 0.85 }}>
                    <Box
                      sx={{
                        position: 'relative',
                        borderRadius: 3,
                        overflow: 'hidden',
                        aspectRatio: '16 / 8.35',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                        background: alpha(theme.palette.primary.main, 0.06),
                      }}
                    >
                      <EmailPreviewThumb
                        alt={row.name}
                        captureUrl={row.captureUrl}
                        directImageUrl={row.directImageUrl}
                        placeholder={row.placeholder || providerLabel}
                        providerLabel={providerLabel}
                        thumbRequest={row.thumbRequest}
                      />
                    </Box>
                  </Box>

                  <CardContent sx={{ display: 'grid', gap: 1 }}>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      <Chip
                        label={providerLabel}
                        size="small"
                        sx={{
                          borderRadius: 999,
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          color: theme.palette.primary.main,
                          fontWeight: 700,
                        }}
                      />
                      <Chip
                        label={formatEmailDate(row.sendDate)}
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: 999, fontWeight: 700 }}
                      />
                    </Stack>

                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 800,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {row.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 0.5,
                          minHeight: 38,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {row.previewText || 'No preview copy available for this email.'}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: 1,
                      }}
                    >
                      {(row.metricItems || []).slice(0, 4).map((item) => (
                        <Box
                          key={`${row.id}-${item.label}`}
                          sx={{
                            p: 0.85,
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                            bgcolor: alpha('#ffffff', 0.72),
                          }}
                        >
                          <Typography variant="overline" sx={{ letterSpacing: 1.1, color: 'text.secondary' }}>
                            {item.label}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                            {formatMetricValue(item.value)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {row.previewHref ? (
                      <Button
                        variant="outlined"
                        size="small"
                        href={row.previewHref}
                        target="_blank"
                        startIcon={<OpenInNewRounded />}
                        sx={{
                          borderRadius: 999,
                          alignSelf: 'flex-start',
                          textTransform: 'none',
                          fontWeight: 700,
                        }}
                      >
                        Open email
                      </Button>
                    ) : null}
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No emails were found in the selected date range.
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export const EmailTableSection = ({
  title,
  subtitle,
  rows = [],
  columns = [],
  emptyMessage,
}) => {
  const theme = useTheme();

  return (
    <Card sx={getPlainSurfaceCardSx(theme)}>
      <CardContent sx={{ display: 'grid', gap: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>

        {rows.length ? (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      data-report-metric={getReportMetricKind(column.key) || getReportMetricKind(column.label)}
                      key={column.key}
                      align={column.align || 'left'}
                      sx={{
                        fontWeight: 800,
                        whiteSpace: 'nowrap',
                        borderBottomColor: alpha(theme.palette.primary.main, 0.12),
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    key={row.id || index}
                    sx={{
                      '&:nth-of-type(odd)': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.03),
                      },
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                      data-report-metric={getReportMetricKind(column.key) || getReportMetricKind(column.label)}
                        key={`${row.id || index}-${column.key}`}
                        align={column.align || 'left'}
                        sx={{ verticalAlign: 'top', borderBottomColor: alpha(theme.palette.primary.main, 0.08) }}
                      >
                        {column.render ? column.render(row) : formatMetricValue(row?.[column.key])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {emptyMessage || 'No rows found.'}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
